import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShoppingBag, Package, Plus, Edit, Trash2, Search, Filter, X, 
  Save, Image as ImageIcon, DollarSign, Tag, Loader2, Eye, EyeOff,
  ClipboardList, CheckCircle, Clock, XCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";
import AdminPagination from "@/components/admin/AdminPagination";

const getImageUrl = (url) => {
  if (!url) return "https://placehold.co/100x100/e2e8f0/64748b?text=Producto";
  if (url.startsWith("http")) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  return url.startsWith("/uploads/") ? `${backendUrl}/api${url}` : `${backendUrl}${url}`;
};

const formatPrice = (price) => {
  return `L ${price?.toLocaleString("es-HN", { minimumFractionDigits: 2 }) || "0.00"}`;
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("es-HN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const PRODUCT_CATEGORIES = [
  { id: "camisetas", name: "Camisetas" },
  { id: "tazas", name: "Tazas" },
  { id: "accesorios", name: "Accesorios" },
  { id: "artesanias", name: "Artesanías" },
  { id: "souvenirs", name: "Souvenirs" },
  { id: "otros", name: "Otros" },
];

const ORDER_STATUS = {
  pending: { label: "Pendiente", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  confirmed: { label: "Confirmado", color: "bg-blue-100 text-blue-800", icon: CheckCircle },
  shipped: { label: "Enviado", color: "bg-purple-100 text-purple-800", icon: Package },
  delivered: { label: "Entregado", color: "bg-green-100 text-green-800", icon: CheckCircle },
  cancelled: { label: "Cancelado", color: "bg-red-100 text-red-800", icon: XCircle },
};

// Product Form Component
function ProductForm({ product, onSave, onClose, categories }) {
  const [formData, setFormData] = useState({
    name: product?.name || "",
    description: product?.description || "",
    price: product?.price || 0,
    compare_price: product?.compare_price || 0,
    stock: product?.stock || 0,
    category: product?.category || "",
    status: product?.status || "draft",
    is_featured: product?.is_featured || false,
    featured_image_id: product?.featured_image_id || "",
    featured_image_url: product?.featured_image_url || "",
  });
  const [saving, setSaving] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaList, setMediaList] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);

  const fetchMedia = async () => {
    setLoadingMedia(true);
    try {
      const response = await axios.get(`${API}/media?limit=50`);
      setMediaList(response.data.items || []);
    } catch (error) {
      console.error("Error fetching media:", error);
    } finally {
      setLoadingMedia(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || formData.price <= 0) {
      toast.error("Por favor completa los campos obligatorios");
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      toast.error("Error al guardar el producto");
    } finally {
      setSaving(false);
    }
  };

  const selectImage = (media) => {
    setFormData({
      ...formData,
      featured_image_id: media.media_id,
      featured_image_url: media.url,
    });
    setShowMediaPicker(false);
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Image */}
        <div>
          <Label>Imagen del Producto</Label>
          <div className="mt-2 flex items-start gap-4">
            <div 
              className="w-32 h-32 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden cursor-pointer hover:border-primary transition-colors"
              onClick={() => {
                fetchMedia();
                setShowMediaPicker(true);
              }}
            >
              {formData.featured_image_url ? (
                <img 
                  src={getImageUrl(formData.featured_image_url)} 
                  alt="Producto"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="text-center p-4">
                  <ImageIcon className="h-8 w-8 text-gray-400 mx-auto mb-1" />
                  <span className="text-xs text-gray-500">Seleccionar</span>
                </div>
              )}
            </div>
            {formData.featured_image_url && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setFormData({ ...formData, featured_image_id: "", featured_image_url: "" })}
              >
                <X className="h-4 w-4 mr-1" />
                Quitar
              </Button>
            )}
          </div>
        </div>

        {/* Name */}
        <div>
          <Label>Nombre del Producto *</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Ej: Camiseta Choluteca"
            className="mt-1"
            required
          />
        </div>

        {/* Description */}
        <div>
          <Label>Descripción</Label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Descripción del producto..."
            className="mt-1"
            rows={3}
          />
        </div>

        {/* Price & Compare Price */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Precio (Lempiras) *</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                className="pl-9"
                min="0"
                step="0.01"
                required
              />
            </div>
          </div>
          <div>
            <Label>Precio Anterior (opcional)</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="number"
                value={formData.compare_price}
                onChange={(e) => setFormData({ ...formData, compare_price: parseFloat(e.target.value) || 0 })}
                className="pl-9"
                min="0"
                step="0.01"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Para mostrar descuento</p>
          </div>
        </div>

        {/* Stock & Category */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Stock Disponible</Label>
            <Input
              type="number"
              value={formData.stock}
              onChange={(e) => setFormData({ ...formData, stock: parseInt(e.target.value) || 0 })}
              className="mt-1"
              min="0"
            />
          </div>
          <div>
            <Label>Categoría</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData({ ...formData, category: value })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Seleccionar" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Status & Featured */}
        <div className="flex items-center justify-between py-4 border-t">
          <div className="flex items-center gap-4">
            <Label>Estado:</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData({ ...formData, status: value })}
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Borrador</SelectItem>
                <SelectItem value="published">Publicado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={formData.is_featured}
              onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
            />
            <Label>Destacado</Label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Guardar Producto
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Media Picker Dialog */}
      <Dialog open={showMediaPicker} onOpenChange={setShowMediaPicker}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Seleccionar Imagen</DialogTitle>
          </DialogHeader>
          {loadingMedia ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-3">
              {mediaList.map((media) => (
                <div
                  key={media.media_id}
                  className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary transition-all"
                  onClick={() => selectImage(media)}
                >
                  <img
                    src={getImageUrl(media.url)}
                    alt={media.title || "Media"}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// Orders Tab Component
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/shop/orders`);
      setOrders(response.data.items || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const formData = new FormData();
      formData.append("status", newStatus);
      await axios.put(`${API}/shop/orders/${orderId}/status`, formData);
      toast.success("Estado actualizado");
      fetchOrders();
      setSelectedOrder(null);
    } catch (error) {
      toast.error("Error al actualizar el estado");
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm("¿Estás seguro de eliminar este pedido? Esta acción no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/shop/orders/${orderId}`);
      toast.success("Pedido eliminado");
      fetchOrders();
      setSelectedOrder(null);
    } catch (error) {
      toast.error("Error al eliminar el pedido");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {orders.length === 0 ? (
        <div className="text-center py-12">
          <ClipboardList className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-600">No hay pedidos</h3>
          <p className="text-gray-500">Los pedidos aparecerán aquí</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Pedido</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Productos</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((order) => {
              const statusInfo = ORDER_STATUS[order.status] || ORDER_STATUS.pending;
              const StatusIcon = statusInfo.icon;
              return (
                <TableRow key={order.order_id}>
                  <TableCell className="font-mono font-medium">
                    {order.order_number}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{order.customer_name}</p>
                      <p className="text-sm text-gray-500">{order.customer_phone}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {order.items?.length || 0} productos
                  </TableCell>
                  <TableCell className="font-bold text-primary">
                    {formatPrice(order.total)}
                  </TableCell>
                  <TableCell>
                    <Badge className={statusInfo.color}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {statusInfo.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-gray-500">
                    {formatDate(order.created_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedOrder(order)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Ver
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => deleteOrder(order.order_id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {/* Order Detail Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Pedido {selectedOrder?.order_number}</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-medium mb-2">Datos del Cliente</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p><strong>Nombre:</strong> {selectedOrder.customer_name}</p>
                  <p><strong>Teléfono:</strong> {selectedOrder.customer_phone}</p>
                  {selectedOrder.customer_email && (
                    <p><strong>Email:</strong> {selectedOrder.customer_email}</p>
                  )}
                  {selectedOrder.customer_address && (
                    <p className="col-span-2"><strong>Dirección:</strong> {selectedOrder.customer_address}</p>
                  )}
                  {selectedOrder.notes && (
                    <p className="col-span-2"><strong>Notas:</strong> {selectedOrder.notes}</p>
                  )}
                </div>
              </div>

              {/* Products */}
              <div>
                <h4 className="font-medium mb-2">Productos</h4>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-sm text-gray-500">
                          {item.quantity} x {formatPrice(item.price)}
                        </p>
                      </div>
                      <p className="font-bold">{formatPrice(item.quantity * item.price)}</p>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center mt-4 pt-4 border-t">
                  <span className="text-lg font-bold">Total:</span>
                  <span className="text-2xl font-bold text-primary">{formatPrice(selectedOrder.total)}</span>
                </div>
              </div>

              {/* Status Update */}
              <div className="pt-4 border-t">
                <h4 className="font-medium mb-2">Actualizar Estado</h4>
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(ORDER_STATUS).map(([key, value]) => (
                    <Button
                      key={key}
                      size="sm"
                      variant={selectedOrder.status === key ? "default" : "outline"}
                      onClick={() => updateOrderStatus(selectedOrder.order_id, key)}
                      className={selectedOrder.status === key ? "" : value.color}
                    >
                      {value.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Delete Order */}
              <div className="pt-4 border-t">
                <Button
                  variant="outline"
                  className="w-full text-red-500 hover:text-red-600 hover:bg-red-50"
                  onClick={() => deleteOrder(selectedOrder.order_id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Eliminar Pedido
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Main Component
export default function AdminTienda() {
  const [activeTab, setActiveTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [categories, setCategories] = useState(PRODUCT_CATEGORIES);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [page, limit]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/shop/products?skip=${(page - 1) * limit}&limit=${limit}`);
      setProducts(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/shop/categories`);
      if (response.data.categories) {
        setCategories(response.data.categories);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleSaveProduct = async (data) => {
    if (editingProduct) {
      await axios.put(`${API}/shop/products/${editingProduct.product_id}`, data);
      toast.success("Producto actualizado");
    } else {
      await axios.post(`${API}/shop/products`, data);
      toast.success("Producto creado");
    }
    fetchProducts();
    setEditingProduct(null);
    setShowForm(false);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("¿Estás seguro de eliminar este producto?")) return;
    try {
      await axios.delete(`${API}/shop/products/${productId}`);
      toast.success("Producto eliminado");
      fetchProducts();
    } catch (error) {
      toast.error("Error al eliminar el producto");
    }
  };

  const filteredProducts = products.filter((p) =>
    p.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-brand-text flex items-center gap-2">
              <ShoppingBag className="h-6 w-6 text-primary" />
              Tienda
            </h1>
            <p className="text-brand-textSecondary">Gestiona tus productos y pedidos</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="products" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Productos
            </TabsTrigger>
            <TabsTrigger value="orders" className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4" />
              Pedidos
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-4">
            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar productos..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button onClick={() => { setEditingProduct(null); setShowForm(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Producto
              </Button>
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-600">No hay productos</h3>
                <p className="text-gray-500 mb-4">Comienza agregando tu primer producto</p>
                <Button onClick={() => { setEditingProduct(null); setShowForm(true); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Agregar Producto
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredProducts.map((product) => (
                  <Card key={product.product_id} className="overflow-hidden">
                    <div className="aspect-square relative">
                      <img
                        src={getImageUrl(product.featured_image_url)}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 right-2 flex gap-1">
                        {product.status === "published" ? (
                          <Badge className="bg-green-500">
                            <Eye className="h-3 w-3 mr-1" />
                            Publicado
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <EyeOff className="h-3 w-3 mr-1" />
                            Borrador
                          </Badge>
                        )}
                      </div>
                      {product.is_featured && (
                        <Badge className="absolute top-2 left-2 bg-amber-500">
                          Destacado
                        </Badge>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-medium text-brand-text truncate">{product.name}</h3>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-lg font-bold text-primary">
                          {formatPrice(product.price)}
                        </span>
                        <span className="text-sm text-gray-500">
                          Stock: {product.stock}
                        </span>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={() => { setEditingProduct(product); setShowForm(true); }}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-500 hover:text-red-600"
                          onClick={() => handleDeleteProduct(product.product_id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <OrdersTab />
          </TabsContent>
        </Tabs>

        {/* Product Form Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingProduct ? "Editar Producto" : "Nuevo Producto"}
              </DialogTitle>
            </DialogHeader>
            <ProductForm
              product={editingProduct}
              onSave={handleSaveProduct}
              onClose={() => { setShowForm(false); setEditingProduct(null); }}
              categories={categories}
            />
          </DialogContent>
        </Dialog>
      </div>
        <AdminPagination
          page={page}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
          testId="tienda-pagination"
          label="productos"
        />
    </AdminLayout>
  );
}
